import { useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Copy, Download, Loader2 } from 'lucide-react';
import type { ConfigField } from '../../services/source.service';
import { useRunSetupAction, useSetupState } from '../../hooks/useSource';
import { apiErrorMessage } from '../../services/api.service';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ConfigFieldDescription } from './ConfigFieldDescription';

interface KeypairRotationFieldProps {
  configField: ConfigField;
  sourceId?: string;
  /** Current form values; account/client ids come from NetSuite and may not be saved yet. */
  config?: Record<string, string>;
}

const EXPIRY_WARNING_DAYS = 30;

function daysUntil(iso?: string | null): number | null {
  if (!iso) return null;
  return Math.floor((new Date(iso).getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}

/**
 * Generates a keypair on our side, hands the customer the public certificate, and
 * rotates active/pending pairs without breaking syncs. Generate only ever writes the
 * pending slot; "Verify & activate" proves the pending pair works against the vendor
 * before promoting it. State/actions come from the connector via
 * GET /sources/:id/setup-state and POST /sources/:id/setup-actions/:action — private
 * keys never reach the browser.
 */
export function KeypairRotationField({ configField, sourceId, config }: KeypairRotationFieldProps) {
  const { data: state, isLoading } = useSetupState(sourceId, !!sourceId && config?.auth_mode !== 'tba');
  const run = useRunSetupAction(sourceId);
  const [certificateId, setCertificateId] = useState('');

  // The certificate only applies to OAuth 2.0 M2M; token-based auth uses static secrets.
  // An unset auth_mode means the default (oauth2_m2m).
  if (config?.auth_mode === 'tba') return null;

  if (!sourceId) {
    return (
      <div className="space-y-2">
        <Label>{configField.label}</Label>
        <p className="text-sm text-muted-foreground">Save this source first, then come back to generate a certificate.</p>
      </div>
    );
  }

  const act = async (action: string, payload?: Record<string, unknown>, success?: string) => {
    try {
      await run.mutateAsync({ action, payload });
      if (success) toast.success(success);
      return true;
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err, 'Action failed'));
      return false;
    }
  };

  const idsPayload = { account_id: config?.account_id ?? '', client_id: config?.client_id ?? '' };
  const missingIds = !idsPayload.account_id.trim() || !idsPayload.client_id.trim();

  const active = state?.active ?? null;
  const pending = state?.pending ?? null;
  const activeDays = daysUntil(active?.expires_at);
  const expiringSoon = activeDays !== null && activeDays <= EXPIRY_WARNING_DAYS;

  const copyCert = async () => {
    if (!pending?.certificate_pem) return;
    try {
      await navigator.clipboard.writeText(pending.certificate_pem);
      toast.success('Certificate copied');
    } catch {
      // Clipboard permission denied / unavailable (e.g. insecure context).
      toast.error('Could not copy to the clipboard. Use Download .pem instead, or select the text and copy it.');
    }
  };

  const downloadCert = () => {
    if (!pending?.certificate_pem) return;
    const url = URL.createObjectURL(new Blob([pending.certificate_pem], { type: 'application/x-pem-file' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pliable-certificate.pem';
    a.click();
    // Some browsers start the download asynchronously; revoking right away can yield an empty file.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="space-y-3 rounded-md border p-4">
      <div className="flex items-center justify-between">
        <Label>{configField.label}</Label>
        {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {state && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {active ? (
            <>
              <Badge variant="secondary" className="gap-1">
                <CheckCircle2 className="h-3 w-3" /> Active
              </Badge>
              <span className="text-muted-foreground">
                Certificate ID {active.certificate_id}
                {active.jwt_algorithm && ` · ${active.jwt_algorithm}`}
                {active.expires_at && ` · expires ${new Date(active.expires_at).toLocaleDateString()}`}
              </span>
              {expiringSoon && (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {activeDays! < 0 ? 'Expired' : `Expires in ${activeDays} days`} — rotate now
                </Badge>
              )}
            </>
          ) : (
            <Badge variant="outline">No active certificate</Badge>
          )}
        </div>
      )}

      {pending ? (
        <div className="space-y-3">
          <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            <li>Upload this certificate in NetSuite: Setup → Integration → Manage Authentication → OAuth 2.0 Client Credentials (M2M) Setup → Create New.</li>
            <li>Copy the Certificate ID NetSuite shows after upload and paste it below.</li>
          </ol>
          <Textarea readOnly rows={6} value={pending.certificate_pem ?? ''} className="font-mono text-xs" />
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={copyCert}>
              <Copy className="mr-1 h-4 w-4" /> Copy
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={downloadCert}>
              <Download className="mr-1 h-4 w-4" /> Download .pem
            </Button>
          </div>
          <div className="space-y-2">
            <Label>New Certificate ID</Label>
            <Input value={certificateId} onChange={(e) => setCertificateId(e.target.value)} placeholder="Certificate ID from NetSuite" />
          </div>
          {missingIds && (
            <p className="text-xs text-destructive">
              Enter the NetSuite Account ID and OAuth 2.0 Client ID above first — NetSuite issues both.
            </p>
          )}
          {active && (
            <p className="text-xs text-muted-foreground">
              The current certificate keeps working until the new one is verified.
            </p>
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              disabled={run.isPending || !certificateId.trim() || missingIds}
              onClick={async () => {
                if (await act('activate_pending', { certificate_id: certificateId, ...idsPayload }, 'Certificate verified and activated')) {
                  setCertificateId('');
                }
              }}
            >
              {run.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Verify &amp; activate
            </Button>
            <Button type="button" variant="ghost" size="sm" disabled={run.isPending} onClick={() => act('discard_pending')}>
              Discard
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button type="button" size="sm" disabled={run.isPending} onClick={() => act('generate_keypair')}>
            {run.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {active ? 'Rotate certificate' : 'Generate certificate'}
          </Button>
          {active && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={run.isPending}
              onClick={() => act('test_connection', idsPayload, 'Connection OK')}
            >
              Test connection
            </Button>
          )}
        </div>
      )}

      <ConfigFieldDescription description={configField.description} />
    </div>
  );
}
